/**
 * @author Mevan Nirosh
 * @version 0.1
 * @since 8/25/2023
 */

import React, { useState, useMemo, useEffect } from "react"
import { MaterialReactTable } from "material-react-table"
import { applicationColumns } from "./applicationColumns"
import api from "../../../../base/services/ResApi"
import { FetchErrorHandeling } from "../../../../base/errorHandeling/FetchErrorHandeling"
import { Box, IconButton, Tooltip } from "@mui/material"
import { Delete } from "@mui/icons-material"
import SearchIcon from "@mui/icons-material/Search"
import DeleteModal from "../../../components/DeleteModals/DeleteModal"
import { RequestErrorHandeling } from "../../../../base/errorHandeling/RequestErrorHandeling"
import Alerts from "../../../../base/errorHandeling/Alerts"
import ApproveApkMoreInfoMadal from "./ApproveApkMoreInfoMadal"
import { Buffer } from "buffer"

const ApprovedAPKTable = ({
  orgCode,
  updateData,
  setUpdateData,
  isSearch,
  setIsSearch,
  filterType,
  filterByVal,
  appIDorType,
}) => {
  const [appRowCount, setAppRowCount] = useState(0)
  const [approvedData, setApprovedData] = useState([])
  const [transformedData, setTransformedData] = useState([])

  const [apprLoading, setApprDataLoading] = useState(false)
  const [rowsData, setRowsData] = useState([])
  const [orgLogo, setOrgLogo] = useState()
  const [approvedOrgData, setApprovedOrgData] = useState([])

  //for error handling
  const [iserror, setIserror] = useState(false)
  const [iserrorShow, setIserrorShow] = useState(false)
  const [isUnauhtError, setIsUnauhtError] = useState(false)
  const [errorMessages, setErrorMessages] = useState([])

  //for success messages
  const [isSuccess, setIsSuccess] = useState(false)
  const [successMessages, setSuccessMessages] = useState([])

  //model States
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [isBtnLoading, setIsBtnLoading] = useState(false)

  //table state
  const [columnFilters, setColumnFilters] = useState([])
  const [globalFilter, setGlobalFilter] = useState("")

  // set open modals
  const [moreInfoModalOpen, setMoreInfoModalOpen] = useState(false)
  const [rowData, setRowData] = useState([])

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  })

  const columns = useMemo(applicationColumns, [])

  let apprvConfig = {
    headers: {
      Action: "V_ALL_APPR_APP",
      "Is-Pagination": true,
      "Page-Number": pagination.pageIndex + 1,
      "Page-Size": pagination.pageSize,
      "Sort-Column": "version_code",
      "Sort-Order": "desc",
      Filter: filterType,
      Value: filterByVal,
      ...(appIDorType ? { "App-Id": appIDorType } : {}),
    },
  }
  let apprvLogoConfig = {
    headers: {
      "Is-Pending": "false",
    },
    responseType: "arraybuffer",
  }

  //Approve Data fetch
  useEffect(() => {
    const fetchData = async () => {
      setApprDataLoading(true)

      try {
        // ✅ Fetch applications
        const appResponse = await api.get("/applications", apprvConfig)
        setApprovedData(appResponse.data?.dataList ?? [])
        setAppRowCount(appResponse.data?.total ?? 0)

        // ✅ Reset errors
        setErrorMessages([])
        setIserror(false)
        setIsUnauhtError(false)

        // ✅ Fetch organization logo
        try {
          const logoResponse = await api.get(
            `/organizations/logo/${orgCode}`,
            apprvLogoConfig
          )
          const convertimg = Buffer.from(logoResponse.data, "binary").toString(
            "base64"
          )
          setOrgLogo(convertimg)
        } catch (logoError) {
          console.error("Logo fetch failed:", logoError)
        }

        // ✅ Fetch organization info
        try {
          const orgResponse = await api.get(`/organizations/${orgCode}`)
          setApprovedOrgData(orgResponse.data?.dataList)
        } catch (orgError) {
          console.error("Org info fetch failed:", orgError)
        }
      } catch (error) {
        FetchErrorHandeling(
          error,
          setIserror,
          setErrorMessages,
          setIserrorShow,
          setIsUnauhtError
        )
      } finally {
        setApprDataLoading(false)
        setIsSearch(false)
        setUpdateData(false)
      }
    }

    if (
      filterType &&
      (isSearch || updateData || pagination.pageIndex || pagination.pageSize)
    ) {
      fetchData()
    }else{
      setIsSearch(false)
    }

  }, [
    isSearch,
    updateData,
    pagination.pageIndex,
    pagination.pageSize,
    isUnauhtError,
  ])


  useEffect(() => {
    const transformedData = approvedData.map((item) => {
      const { tmnlAppBroadcastDtl, appSummaryDtl, ...rest } = item

      return {
        ...rest,
        appSummaryDtl,
        appUploadedBy: tmnlAppBroadcastDtl?.appUploadedBy,
        appUploadedDateTime: tmnlAppBroadcastDtl?.appUploadedDateTime,
        lastUpdatedBy: tmnlAppBroadcastDtl?.lastUpdatedBy,
        lastUpdatedDateTime: tmnlAppBroadcastDtl?.lastUpdatedDateTime,
        tmnlAppStatus: tmnlAppBroadcastDtl?.tmnlAppStatus,
        totInstallCount: appSummaryDtl?.totInstallCount,
        totTerminals: appSummaryDtl?.totTerminals,
      }
    })

    setTransformedData(transformedData)
  }, [approvedData])

  //remove error message after some time
  useEffect(() => {
    if (iserror === true) {
      setTimeout(() => {
        setIserror(false)
        setErrorMessages([])
      }, 10000)
    }
  }, [iserror])

  //remove success message after some time
  useEffect(() => {
    if (isSuccess === true) {
      setTimeout(() => {
        setIsSuccess(false)
        setSuccessMessages([])
      }, 5000)
    }
  }, [isSuccess])

  // delete model api
  const handleDelete = () => {
    setIsBtnLoading(true)
    const appId = rowsData.applicationId

    api
      .delete("/applications/" + appId)
      .then((res) => {
        setIserror(false)
        setErrorMessages([])
        setIsUnauhtError(false)
        setIsSuccess(true)
        setSuccessMessages([res.data?.description])
        setUpdateData(true)
        setIsBtnLoading(false)
        setDeleteModalOpen(false)
      })
      .catch((error) => {
        setIsBtnLoading(false)
        setIsSuccess(false)
        setDeleteModalOpen(false)
        RequestErrorHandeling(
          error,
          setIserror,
          setErrorMessages,
          setIserrorShow,
          setIsUnauhtError
        )
      })
  }

  return (
    <div>
      <div>
        <Alerts
          iserror={iserror}
          errorMessages={errorMessages}
          isSuccess={isSuccess}
          successMessages={successMessages}
          iserrorShow={iserrorShow}
        />
      </div>
      <br />
      <Box sx={{ display: "grid" }}>
        <MaterialReactTable
          enableColumnVirtualization
          columns={columns}
          data={transformedData ?? []}
          state={{
            columnFilters,
            globalFilter,
            pagination,
            showProgressBars: apprLoading,
          }}
          getRowId={(row) => String(row.applicationId)}
          initialState={{
            showColumnFilters: false,
            columnVisibility: {
              appId: false,
              versionCode: false,
              description: false,
              createdDateTime: false,
              appLogo: false,
            },
          }}
          manualPagination
          onColumnFiltersChange={setColumnFilters}
          onGlobalFilterChange={setGlobalFilter}
          onPaginationChange={setPagination}
          rowCount={appRowCount}
          muiTablePaginationProps={{
            rowsPerPageOptions: [10],
          }}
          enableColumnOrdering
          enableGrouping
          enableRowActions
          renderRowActions={({ row }) => (
            <Box>
              <Tooltip arrow placement="right" title="Delete">
                <IconButton
                  aria-label="Delete application"
                  color="error"
                  onClick={() => {
                    setDeleteModalOpen(true)
                    setRowsData(row.original)
                  }}
                >
                  <Delete />
                </IconButton>
              </Tooltip>
              <Tooltip arrow placement="left" title="More Stats">
                <IconButton color="success" aria-label="View application details" onClick={() => {
                  setMoreInfoModalOpen(true)
                  setRowData(row.original)
                }}>
                  <SearchIcon />
                </IconButton>
              </Tooltip>
            </Box>
          )}
          enableColumnResizing
          columnResizeMode="onEnd"
        />
      </Box>

      <DeleteModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onDelete={handleDelete}
        isBtnLoading={isBtnLoading}
      />
      <ApproveApkMoreInfoMadal
        open={moreInfoModalOpen}
        onClose={() => setMoreInfoModalOpen(false)}
        approvedData={rowData}
        orgLogo={orgLogo}
        approvedOrgData={approvedOrgData[0]}
      />
    </div>
  )
}

export default ApprovedAPKTable
